import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, UserPlus, Pencil, Trash2 } from "lucide-react";

export const SubAdminsTab = ({
  darkMode, subAdmins, removeSubAdmin,
  setEditingSubAdmin, setSubAdminForm, setSubAdminDialogOpen
}) => {
  const cardBg = darkMode ? "bg-gray-800/80 border border-gray-700/50" : "";

  return (
    <Card className={`border-0 shadow-soft rounded-2xl ${cardBg}`}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className={`flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}>
          <Shield className={`w-5 h-5 ${darkMode ? "text-blue-400" : "text-primary"}`} /> Sub-Admin Management
        </CardTitle>
        <Button
          className="rounded-xl"
          data-testid="invite-sub-admin-btn"
          onClick={() => {
            setEditingSubAdmin(null);
            setSubAdminForm({ email: "", name: "", password: "", permissions: [] });
            setSubAdminDialogOpen(true);
          }}
        >
          <UserPlus className="w-4 h-4 mr-2" /> Invite Sub-Admin
        </Button>
      </CardHeader>
      <CardContent>
        {subAdmins.length === 0 ? (
          <div className="text-center py-12">
            <Shield className={`w-12 h-12 mx-auto mb-3 ${darkMode ? "text-gray-600" : "text-gray-300"}`} />
            <p className={`mb-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>No sub-admins yet</p>
            <p className={`text-sm ${darkMode ? "text-gray-500" : "text-gray-400"}`}>Invite team members to help manage the platform</p>
          </div>
        ) : (
          <div className="space-y-4">
            {subAdmins.map(sa => (
              <div key={sa.id} className={`border rounded-2xl p-5 hover:shadow-soft transition-shadow ${darkMode ? "border-gray-700 bg-gray-800/50 hover:bg-gray-700/50" : ""}`} data-testid={`sub-admin-card-${sa.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className={`font-semibold ${darkMode ? "text-gray-100" : "text-navy-900"}`}>{sa.name || "Unnamed"}</h3>
                    <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{sa.email}</p>
                    {sa.last_active && (
                      <p className={`text-xs mt-1 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>Last active: {new Date(sa.last_active).toLocaleDateString()}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className={`rounded-xl ${darkMode ? "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600" : ""}`}
                      data-testid={`edit-sub-admin-${sa.id}`}
                      onClick={() => {
                        setEditingSubAdmin(sa);
                        setSubAdminForm({ email: sa.email, name: sa.name, password: "", permissions: sa.permissions || [], view_only_permissions: sa.view_only_permissions || [], access_code: sa.access_code || "" });
                        setSubAdminDialogOpen(true);
                      }}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className={`rounded-xl ${darkMode ? "text-red-400 hover:text-red-300 border-gray-600 hover:bg-red-900/20" : "text-red-600 hover:text-red-700 hover:bg-red-50"}`}
                      data-testid={`remove-sub-admin-${sa.id}`}
                      onClick={() => removeSubAdmin(sa.id)}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(sa.permissions || []).length === 0 ? (
                    <Badge variant="outline" className={`rounded-lg ${darkMode ? "text-gray-500 border-gray-600" : "text-gray-400"}`}>No permissions</Badge>
                  ) : (
                    (sa.permissions || []).map(p => {
                      const isViewOnly = (sa.view_only_permissions || []).includes(p);
                      return (
                        <Badge key={p} className={`rounded-lg border-0 text-xs ${isViewOnly ? (darkMode ? "bg-amber-900/30 text-amber-300" : "bg-amber-100 text-amber-700") : (darkMode ? "bg-blue-900/30 text-blue-300" : "bg-primary/10 text-primary")}`}>
                          {p.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                          {isViewOnly ? " (View Only)" : ""}
                        </Badge>
                      );
                    })
                  )}
                </div>
                {sa.access_code && (
                  <Badge className={`rounded-lg border-0 text-xs mt-1.5 ${sa.access_code_verified_at ? (darkMode ? "bg-green-900/30 text-green-300" : "bg-green-100 text-green-700") : (darkMode ? "bg-yellow-900/30 text-yellow-300" : "bg-yellow-100 text-yellow-700")}`}>
                    {sa.access_code_verified_at ? "Code Verified" : "Code Pending"}
                  </Badge>
                )}
                {sa.invited_by && (
                  <p className={`text-xs mt-3 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>Invited by {sa.invited_by} on {sa.created_at ? new Date(sa.created_at).toLocaleDateString() : "N/A"}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
